import { Add, Error } from "@mui/icons-material";
import { Box, Button, GlobalStyles } from "@mui/material";
import { App, Route, UniversalAppState } from "@pimo/pimo-app-builder";
import {
  CustomFormFields,
  DefaultOverlayPartialAppState,
  PimoFormDialog,
  PMPopUp,
  sortByCustomIdAndName,
} from "@pimo/pimo-components";
import { FormFieldDefinition } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/form-field";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import { generatePath } from "react-router-dom";
import {
  APP_CRITICALITIES,
  APP_LIFE_CYCLES,
  APPLICATION_CAPABILITIES,
  Attachment,
  BasicItem,
  getChannelLabel,
  getDistributionChannels,
  ITEM_NAMES,
  PROCESS_STEPS,
  Project,
  ReportingItem,
  TECHNICAL_EXPOSURE,
  VISIBLE_APP_RECORD_TYPES,
} from "vmd-types";
import {
  CHARACTER_LIMITATIONS,
  getFieldValidation,
  validateReportUpdateForm,
} from "vmd-utils";

import { TestAppState } from "../app";
import { APP_ROUTES } from "../constants";
import {
  deleteAttachment,
  downloadAttachment,
  fetchAttachmentsByReportingItem,
  removeTemporaryAttachments,
  uploadAttachment,
} from "../helpers/fetch-attachments";
import { deleteBasicItem } from "../helpers/fetch-basic-items";
import { fetchEntityConfiguration } from "../helpers/fetch-entity-configurations";
import { fetchProject, updateProject } from "../helpers/fetch-project";
import { showSnackbar } from "../helpers/show-snackbar";
import { theme } from "../theme";
import { ensurePrimaryNotInSecondary } from "../utils/ensure-primary-not-in-secondary";
import { getPluralOfItemNameFromState } from "../utils/get-plural-of-item-name";
import { orderDistributionChannels } from "../utils/order-distribution-channels";
import {
  FormMethodsLike,
  setupFormFieldWatcher,
} from "../utils/setup-form-field-watcher";
import {
  ACTION_BUTTON_FIELD_TYPE,
  ActionButtonFormField,
  APP_STICKY_HEADER_CLASS,
} from "./custom-form-fields/action-button-field";
import {
  MEMO_RICH_TEXT_FIELD_TYPE,
  MemoRichTextFormField,
} from "./custom-form-fields/memo-rich-text-field";
import {
  ACTIVE_APP_TAB_FIELD,
  REPORT_TAB_VISIBILITY_FIELD_TYPE,
  ReportTabVisibilityField,
} from "./custom-form-fields/report-tab-visibility-field";

const STYLE_TEXT = {
  "& .MuiInputBase-input": {
    fontSize: "14px",
  },
  // multiline textarea
  "& .MuiInputBase-inputMultiline, & textarea": {
    fontSize: "14px",
  },
  // label size (optional)
  "& .MuiInputLabel-root": {
    fontSize: "14px",
  },
  fontSize: "14px",
};

const CUSTOM_FIELDS = {
  [ACTION_BUTTON_FIELD_TYPE]: ActionButtonFormField,
  [REPORT_TAB_VISIBILITY_FIELD_TYPE]: ReportTabVisibilityField,
  [MEMO_RICH_TEXT_FIELD_TYPE]: MemoRichTextFormField,
} as const satisfies CustomFormFields<
  | typeof ACTION_BUTTON_FIELD_TYPE
  | typeof REPORT_TAB_VISIBILITY_FIELD_TYPE
  | typeof MEMO_RICH_TEXT_FIELD_TYPE
>;

// A structural sink for the per-app field builders. A real FormTab satisfies it, and so does the
// gated wrapper returned by withActiveAppTabGate, which injects the active-tab visibility gate.
type FieldSink = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addFormField: (field: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addLayoutElement: (element: any) => void;
};

function withActiveAppTabGate(tab: FieldSink, appTabId: string): FieldSink {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gate = (def: any) => {
    const existing = def?.isVisible as
      ((formData: Record<string, unknown>) => boolean) | undefined;
    return {
      ...def,
      isVisible: (formData: Record<string, unknown>) =>
        formData?.[ACTIVE_APP_TAB_FIELD] === appTabId &&
        (existing ? existing(formData) : true),
    };
  };
  return {
    addFormField: (field) => tab.addFormField(gate(field)),
    addLayoutElement: (element) => tab.addLayoutElement(gate(element)),
  };
}

const REQUIRED_APP_TEXT_FIELDS: { key: string; label: string }[] = [
  { key: "name", label: "Application Name" },
  { key: "distributionChannel", label: "Primary Distribution Channel" },
  { key: "customId", label: "Archer ID" },
  { key: "owner", label: "Application Owner" },
  { key: "recordType", label: "Record Type" },
  { key: "description", label: "Application Description" },
  { key: "criticality", label: "Criticality of the Application" },
  { key: "lifeCycle", label: "Life Cycle" },
  { key: "hasContracts", label: "Contracts" },
];
const REQUIRED_APP_ARRAY_FIELDS: { key: string; label: string }[] = [
  { key: "processSteps", label: "Process Steps" },
  { key: "applicationCapabilities", label: "Application Capabilities" },
  { key: "technicalExposure", label: "Technical Exposure" },
];

const ALL_REQUIRED_APP_FIELD_NAMES = [
  ...REQUIRED_APP_TEXT_FIELDS,
  ...REQUIRED_APP_ARRAY_FIELDS,
].map(({ key }) => `newApplication.${key}`);

function setNewAppFieldErrors(
  formMethods: unknown,
  missingKeys: string[],
  message: string
): void {
  const methods = formMethods as
    | {
        setError?: (
          name: string,
          error: { type: string; message: string },
          options?: { shouldFocus?: boolean }
        ) => void;
        clearErrors?: (names: string[]) => void;
      }
    | undefined;
  methods?.clearErrors?.(ALL_REQUIRED_APP_FIELD_NAMES);
  missingKeys.forEach((key, i) =>
    methods?.setError?.(
      `newApplication.${key}`,
      { type: "required", message },
      { shouldFocus: i === 0 }
    )
  );
}

function getMissingRequiredAppFields(
  values:
    | (Partial<BasicItem> & { distributionChannel?: string | string[] })
    | undefined
): string[] {
  const data = (values ?? {}) as Record<string, unknown>;
  const missing: string[] = [];
  for (const { key } of REQUIRED_APP_TEXT_FIELDS) {
    const value = data[key];
    if (typeof value !== "string" || value.trim() === "") missing.push(key);
  }
  for (const { key } of REQUIRED_APP_ARRAY_FIELDS) {
    const value = data[key];
    if (!Array.isArray(value) || value.length === 0) missing.push(key);
  }
  return missing;
}

function buildAppTabLabel(
  customId: string | undefined,
  name: string | undefined
): string {
  const rawLabel = `${customId ?? ""} - ${name ?? ""}`;
  const contentLength = (customId?.length ?? 0) + (name?.length ?? 0);
  return (
    contentLength < 22 ? rawLabel : `${rawLabel.substring(0, 25)}...`
  ).trim();
}

export async function buildFormRoute(
  app: App<TestAppState>,
  parentRoute: Route<"id" | "viewname", TestAppState>
) {
  const dialog = app.createView({
    name: "edit-subentity",
  });

  const editForm = new PimoFormDialog<Project, typeof CUSTOM_FIELDS>();
  editForm.registerFormField(
    ACTION_BUTTON_FIELD_TYPE,
    CUSTOM_FIELDS[ACTION_BUTTON_FIELD_TYPE]
  );
  editForm.registerFormField(
    REPORT_TAB_VISIBILITY_FIELD_TYPE,
    CUSTOM_FIELDS[REPORT_TAB_VISIBILITY_FIELD_TYPE]
  );
  editForm.registerFormField(
    MEMO_RICH_TEXT_FIELD_TYPE,
    CUSTOM_FIELDS[MEMO_RICH_TEXT_FIELD_TYPE]
  );

  const form = dialog.addComponent({ component: editForm });

  let pendingSavePayload: Project | undefined;
  let rebuildTabs: (() => Promise<void>) | undefined;

  let isCreatingApp = false;
  let isSaving = false;

  let closeAfterSaveRequested = false;
  const pendingNewAppFiles = new Map<string, File[]>();
  let stopChannelWatchers: (() => void) | undefined;

  const teardownChannelWatchers = () => {
    stopChannelWatchers?.();
    stopChannelWatchers = undefined;
  };

  const sessionTempAttachmentItemIds = new Set<string>();
  const uploadAttachmentTracked = async (entityId: string, file: File) => {
    sessionTempAttachmentItemIds.add(String(entityId));
    return uploadAttachment(entityId, file);
  };

  const forceFormRerender = () => {
    const state = app.getAppState();
    if (state.currentReport) {
      app.patchAppState({ currentReport: { ...state.currentReport } });
    }
  };

  const builtAppTabIds = new Set<string>();
  const deepLinkTabId = new URLSearchParams(window.location.search).get(
    "tabId"
  );
  if (deepLinkTabId) builtAppTabIds.add(deepLinkTabId);

  const appLabelByTabId = new Map<string, string>();
  const uncappedChannelBoxes = new Set<HTMLElement>();

  const getAppTabRail = (): HTMLElement | undefined =>
    (document.querySelector<HTMLElement>(".vmd-add-application-slot")
      ?.parentElement?.parentElement?.firstElementChild as HTMLElement) ??
    undefined;

  const findRowByText = (text: string): HTMLElement | undefined => {
    const rail = getAppTabRail();
    if (!rail) return undefined;
    for (const el of Array.from(rail.querySelectorAll<HTMLElement>("div"))) {
      if (el.textContent?.trim() === text) return el;
    }
    return undefined;
  };

  const capChannelBox = (box: HTMLElement) => {
    if (!uncappedChannelBoxes.has(box)) return;
    uncappedChannelBoxes.delete(box);
    box.style.removeProperty("max-height");
    box.style.removeProperty("overflow");
  };

  const uncapChannelBox = (box: HTMLElement) => {
    if (uncappedChannelBoxes.has(box)) return;
    uncappedChannelBoxes.add(box);
    box.style.setProperty("max-height", "none");
    box.style.setProperty("overflow", "visible");
  };

  const syncUncapForActiveTab = (appTabId: string) => {
    const label = appLabelByTabId.get(appTabId);
    const box = label ? findRowByText(label)?.parentElement : undefined;
    if (!(box instanceof HTMLElement)) return;
    for (const other of Array.from(uncappedChannelBoxes)) {
      if (other !== box) capChannelBox(other);
    }
    uncapChannelBox(box);
  };

  document.addEventListener(
    "click",
    (event) => {
      const button = (event.target as HTMLElement | null)?.closest?.(
        "button.MuiIconButton-root"
      );
      if (!button) return;
      const rail = getAppTabRail();
      if (!rail || !rail.contains(button)) return;
      const header = button.parentElement;
      const box = header?.nextElementSibling;
      if (!(box instanceof HTMLElement)) return;
      if (uncappedChannelBoxes.has(box)) {
        capChannelBox(box);
      } else {
        uncapChannelBox(box);
        if (header instanceof HTMLElement) {
          requestAnimationFrame(() =>
            header.scrollIntoView({ behavior: "smooth", block: "start" })
          );
        }
      }
    },
    { capture: true }
  );

  let activeAppTabId: string | undefined;
  const onAppTabActivated = (appTabId: string) => {
    if (appTabId === activeAppTabId) return;
    activeAppTabId = appTabId;
    const afterSettled = () =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() => syncUncapForActiveTab(appTabId))
      );
    if (!builtAppTabIds.has(appTabId)) {
      builtAppTabIds.add(appTabId);
      void rebuildTabs?.().then(afterSettled);
      return;
    }
    forceFormRerender();
    afterSettled();
  };

  let computeFormErrors:
    | ((
        data: Omit<Project, "id">,
        language: string
      ) => Record<string, { type: string; message: string }>)
    | undefined;

  registerFormEvents();

  async function performSave(payload: Project): Promise<boolean> {
    const projectId = payload.id!;
    // Render-only marker (see report-tab-visibility-field); never persist it.
    delete (payload as unknown as Record<string, unknown>)[
      ACTIVE_APP_TAB_FIELD
    ];
    const updatedProject = await updateProject(projectId, payload, true);

    if (!updatedProject) {
      showSnackbar(
        app as unknown as App<
          UniversalAppState & DefaultOverlayPartialAppState
        >,
        "Saving failed. Please try again.",
        "error"
      );
      return false;
    }

    app.patchAppState({
      currentReport: {
        project: updatedProject,
      },
    });
    // Upload contracts buffered for apps just created in this Save (the upload endpoint stores them as
    // permanent, so nothing further is needed to keep them).
    if (pendingNewAppFiles.size > 0) {
      for (const ri of updatedProject.reportingItems ?? []) {
        const key = `${(ri.distributionChannel ?? []).join("|") || ""}|${
          ri.basicItem?.name?.trim() ?? ""
        }`;
        const files = pendingNewAppFiles.get(key);
        if (!files || !ri.id) continue;
        for (const file of files) {
          await uploadAttachment(String(ri.id), file);
        }
        pendingNewAppFiles.delete(key);
      }
    }
    // Re-align form values and tabs with the server result so a follow-up save can't diverge.
    const setValue = editForm.getFormMethods()?.setValue as unknown as
      ((name: string, value: unknown) => void) | undefined;
    setValue?.("reportingItems", updatedProject.reportingItems ?? []);
    await rebuildTabs?.();
    showSnackbar(
      app as unknown as App<UniversalAppState & DefaultOverlayPartialAppState>,
      "All changes have been saved.",
      "success"
    );
    return true;
  }

  function registerFormEvents() {
    const runSubmit = async (payload: Project): Promise<boolean> => {
      // A filled-in Add Application form is persisted as part of Save, not silently dropped.
      const newApplication = (
        payload as unknown as {
          newApplication?: Partial<BasicItem> & {
            distributionChannel?: string;
          };
        }
      ).newApplication;
      delete (payload as unknown as { newApplication?: unknown })
        .newApplication;

      const naChannel = newApplication?.distributionChannel;
      const naName = newApplication?.name?.trim();
      const alreadyStaged = (payload.reportingItems ?? []).some(
        (ri) =>
          !ri.basicItem?.id &&
          JSON.stringify(ri.distributionChannel) ===
            JSON.stringify(naChannel) &&
          ri.basicItem?.name?.trim() === naName
      );
      if (naChannel && naName && !alreadyStaged) {
        const missingDraftFields = getMissingRequiredAppFields(newApplication);
        if (missingDraftFields.length > 0) {
          setNewAppFieldErrors(
            editForm.getFormMethods(),
            missingDraftFields,
            getLocalizedMessage(
              app.getAppState().language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required"
          );
          return false;
        }
        const basicItemValues = { ...newApplication };
        delete basicItemValues.distributionChannel;
        // The primary can never also be a secondary channel.
        if (Array.isArray(basicItemValues.otherDistributionChannels)) {
          basicItemValues.otherDistributionChannels =
            basicItemValues.otherDistributionChannels.filter(
              (c) => c !== naChannel
            );
        }

        // Build the new app with no id; the project Save creates it (no eager POST).
        const newItem = {
          distributionChannel: Array.isArray(naChannel)
            ? naChannel
            : [naChannel].filter(Boolean),
          isChannelHeader: false,
          basicItem: { ...basicItemValues, name: naName },
        } as unknown as ReportingItem;
        payload.reportingItems = [...(payload.reportingItems ?? []), newItem];

        // Clear the form so the next Save can't re-create the same app.
        const setValue = editForm.getFormMethods()?.setValue as unknown as
          ((name: string, value: unknown) => void) | undefined;
        setValue?.("newApplication", {
          distributionChannel: "",
          name: "",
          customId: "",
          owner: "",
          recordType: "",
          description: "",
          criticality: "",
          lifeCycle: "",
          hasContracts: "",
          otherDistributionChannels: [],
          processSteps: [],
          applicationCapabilities: [],
          technicalExposure: [],
        });
      }

      const selectedChannels = new Set(payload.distributionChannels ?? []);
      for (const ri of payload.reportingItems ?? []) {
        const others = ri.basicItem?.otherDistributionChannels;
        if (Array.isArray(others) && ri.basicItem) {
          ri.basicItem.otherDistributionChannels = others.filter((c) =>
            selectedChannels.has(c)
          );
        }
      }
      const appsToDelete = (payload.reportingItems ?? []).filter(
        (ri) =>
          !ri.isChannelHeader &&
          ri.distributionChannel &&
          !ri.distributionChannel.some((c) => selectedChannels.has(c))
      );

      if (appsToDelete.length > 0) {
        pendingSavePayload = payload;
        app.patchAppState({
          showChannelDeletionDialog: true,
          pendingChannelDeletionCount: appsToDelete.length,
        });
        // Save deferred to the channel-deletion confirmation; don't close yet.
        return false;
      }

      return await performSave(payload);
    };

    form.on("submit", async ({ payload = {} as Project }) => {
      if (!payload.id) {
        return;
      }
      if (isSaving || isCreatingApp) return;
      isSaving = true;
      let saved = false;
      try {
        // The default save only validates the current tab; re-validate every tab (validateFormData runs
        // over the whole form data, so incomplete apps are caught even when their tab isn't mounted) and
        // block the save while surfacing the field errors.
        const formMethods = editForm.getFormMethods() as unknown as
          { areAllTabsValid?: () => Promise<boolean> } | undefined;
        if (
          formMethods?.areAllTabsValid &&
          !(await formMethods.areAllTabsValid())
        ) {
          // Keep the form open showing the errors instead of completing a deferred save-and-close.
          closeAfterSaveRequested = false;
          return;
        }
        // areAllTabsValid only checks built tabs; with app-tab pagination an incomplete app can live
        // on an unbuilt page. Re-validate the whole payload so those still block the save.
        const crossPageErrors =
          computeFormErrors?.(payload, app.getAppState().language || "en") ??
          {};
        if (Object.keys(crossPageErrors).length > 0) {
          const setError = editForm.getFormMethods()?.setError as
            | ((name: string, error: { type: string; message: string }) => void)
            | undefined;
          Object.entries(crossPageErrors).forEach(([name, error]) =>
            setError?.(name, error)
          );
          showSnackbar(
            app as unknown as App<
              UniversalAppState & DefaultOverlayPartialAppState
            >,
            "Some required fields are missing on other pages. Use the sidebar pager to find and complete them.",
            "error"
          );
          closeAfterSaveRequested = false;
          return;
        }
        saved = await runSubmit(payload);
      } finally {
        isSaving = false;
      }
      // save-and-close requested a close while this save (incl. new-app contract uploads) was
      // running. Now that it's done, honor it — but only if the save actually succeeded, so a
      // failed save keeps the form open with its error message.
      if (closeAfterSaveRequested) {
        closeAfterSaveRequested = false;
        if (saved) closeEditForm();
      }
    });

    form.on("submit-without-changes", async () => {
      closeEditForm();
    });

    form.on("cancel", () => closeEditFormWithCleanup());
    form.on("cancel-with-changes", () =>
      app.patchAppState({ showEditFormConfirmationDialog: true })
    );
    form.on("close", async () => {
      if (app.getAppState().showChannelDeletionDialog) {
        return;
      }
      // save-and-close fires `submit` immediately before this `close`. The in-flight save uploads
      // the new app's buffered contracts, so let it finish and navigate (see the submit handler)
      // rather than clearing pendingNewAppFiles out from under it here.
      if (isSaving) {
        closeAfterSaveRequested = true;
        return;
      }
      await closeEditFormWithCleanup();
    });
  }
  function closeEditForm() {
    teardownChannelWatchers();
    const currentProject = app.getAppState().currentReport?.project;

    const resourceLockPlugin =
      app.getPluginByName<ResourceLockPlugin<TestAppState>>(
        "ResourceLockPlugin"
      );

    resourceLockPlugin?.unlock(`project-${currentProject?.id}`);

    if (currentProject?.id == null) {
      app.navigate(APP_ROUTES.overview);
      return;
    }

    app.navigate(
      generatePath(APP_ROUTES.report, {
        id: String(currentProject.id),
        viewname: "dashboard",
      })
    );
  }

  async function closeEditFormWithCleanup() {
    // Drop contracts held for apps that were never saved.
    pendingNewAppFiles.clear();

    await Promise.all(
      [...sessionTempAttachmentItemIds].map((id) =>
        removeTemporaryAttachments(id)
      )
    );
    sessionTempAttachmentItemIds.clear();

    closeEditForm();
  }

  function createConfirmationDialog() {
    const confirmationDialog = dialog.addComponent({
      component: PMPopUp,
    });

    confirmationDialog.mapVisibility(
      ({ showEditFormConfirmationDialog }) =>
        showEditFormConfirmationDialog === true
    );

    confirmationDialog.mapState(() => {
      const language = app.getAppState().language || "en";
      return {
        title:
          getLocalizedMessage(language, "dialog.title.unsavedChanges", {
            entity: "form",
          }) || "Unsaved Changes",
        description:
          getLocalizedMessage(language, "dialog.message.unsavedChanges", {
            entity: "form",
          }) ||
          "You have made changes that have not been saved. If you close the settings, these changes will be lost.",
        maxWidth: "sm",

        cancelButtonText:
          getLocalizedMessage(language, "dialog.button.cancel") || "Cancel",
        cancelButtonColor: "white",
        confirmationButtonColor: "blue",
        confirmationButtonText:
          getLocalizedMessage(language, "dialog.button.continue") || "Continue",

        cancelButtonFireEvent: () => closeConfirmationDialog(),
        confirmationButtonFireEvent: async () => {
          closeConfirmationDialog();
          await closeEditFormWithCleanup();
        },
        onClose: () => closeConfirmationDialog(),
      };
    });

    function closeConfirmationDialog() {
      app.patchAppState({
        showEditFormConfirmationDialog: false,
      });
    }
  }

  function createDeleteAppDialog() {
    const deleteDialog = dialog.addComponent({
      component: PMPopUp,
    });

    deleteDialog.mapVisibility(
      ({ showDeleteAppDialog }) => showDeleteAppDialog === true
    );

    deleteDialog.mapState(() => {
      const language = app.getAppState().language || "en";
      const itemName =
        app.getAppState().settings?.configuration?.itemName ?? "Tracking Item";
      return {
        title: `Delete ${itemName}`,
        description: `Are you sure you want to delete this ${itemName.toLowerCase()}? This action cannot be undone.`,
        maxWidth: "sm",
        cancelButtonText:
          getLocalizedMessage(language, "dialog.button.cancel") || "Cancel",
        cancelButtonColor: "white",
        confirmationButtonColor: "error",
        confirmationButtonText:
          getLocalizedMessage(language, "delete") || "Delete",
        cancelButtonFireEvent: () => closeDeleteAppDialog(),
        confirmationButtonFireEvent: async () => {
          const id = app.getAppState().pendingDeleteAppId;
          closeDeleteAppDialog();
          if (id != null) {
            await deleteBasicItem(String(id));
            closeEditForm();
          }
        },
        onClose: () => closeDeleteAppDialog(),
      };
    });

    function closeDeleteAppDialog() {
      app.patchAppState({
        showDeleteAppDialog: false,
        pendingDeleteAppId: undefined,
      });
    }
  }

  function createChannelDeletionDialog() {
    const channelDeletionDialog = dialog.addComponent({
      component: PMPopUp,
    });

    channelDeletionDialog.mapVisibility(
      ({ showChannelDeletionDialog }) => showChannelDeletionDialog === true
    );

    channelDeletionDialog.mapState(() => {
      const language = app.getAppState().language || "en";
      const count = app.getAppState().pendingChannelDeletionCount ?? 0;
      const singular = (
        app.getAppState().settings?.configuration?.itemName ?? "Tracking Item"
      ).toLowerCase();
      const plural = getPluralOfItemNameFromState(
        app.getAppState()
      ).toLowerCase();
      return {
        title: "Remove distribution channel",
        description: `Removing this channel will also permanently delete ${count} ${
          count === 1 ? singular : plural
        } reported under it. This action cannot be undone.`,
        maxWidth: "sm",
        cancelButtonText:
          getLocalizedMessage(language, "dialog.button.cancel") || "Cancel",
        cancelButtonColor: "white",
        confirmationButtonColor: "error",
        confirmationButtonText:
          getLocalizedMessage(language, "delete") || "Delete",
        cancelButtonFireEvent: () => closeChannelDeletionDialog(),
        confirmationButtonFireEvent: async () => {
          const payload = pendingSavePayload;
          closeChannelDeletionDialog();
          if (payload && (await performSave(payload))) {
            closeEditForm();
          }
        },
        onClose: () => closeChannelDeletionDialog(),
      };
    });

    function closeChannelDeletionDialog() {
      pendingSavePayload = undefined;
      app.patchAppState({
        showChannelDeletionDialog: false,
        pendingChannelDeletionCount: undefined,
      });
    }
  }

  const route = parentRoute.createChildRoute<"id">({
    path: APP_ROUTES.editReport,
    view: dialog,
    isOverlayChild: true,
  });
  createConfirmationDialog();
  createDeleteAppDialog();
  createChannelDeletionDialog();

  route.on("load", async ({ payload }) => {
    const id = payload?.parameters?.id;
    if (!id) {
      return;
    }
    teardownChannelWatchers();
    pendingSavePayload = undefined;

    const existingReport = app.getAppState().currentReport;
    const currentProject =
      existingReport?.project?.id != null &&
      String(existingReport.project.id) === String(id)
        ? existingReport
        : await fetchProject({ id: payload.parameters.id! });
    const configuration = await fetchEntityConfiguration();
    const itemName = configuration?.itemName ?? "Tracking Item";
    const addApplicationTabLabel = `+ Add ${itemName}`;

    computeFormErrors = (data, language) => {
      const requiredMessage =
        getLocalizedMessage(language, "validationErrors.fieldRequired") ||
        "This field is required";
      // Runs on the full form data, so every app is validated even when its tab isn't built.
      const errors: Record<string, { type: string; message: string }> = {
        ...(validateReportUpdateForm(data, configuration, {
          fieldRequired: requiredMessage,
        }) as Record<string, { type: string; message: string }>),
      };
      const totalRequired =
        REQUIRED_APP_TEXT_FIELDS.length + REQUIRED_APP_ARRAY_FIELDS.length;
      (data.reportingItems ?? []).forEach((ri, idx) => {
        if (ri.isChannelHeader || ri.basicItem == null || ri.basicItem.id)
          return;
        getMissingRequiredAppFields({
          ...ri.basicItem,
          distributionChannel: (ri.distributionChannel?.join(",") ??
            undefined) as unknown as string,
        }).forEach((key) => {
          errors[
            key === "distributionChannel"
              ? `reportingItems.[${idx}].distributionChannel`
              : `reportingItems.[${idx}].basicItem.${key}`
          ] = { type: "required", message: requiredMessage };
        });
      });
      // The Add Application draft: validate only once the user has started it (at least one
      // required field filled), so an untouched draft never blocks the report save.
      const draft = (
        data as {
          newApplication?: Partial<BasicItem> & {
            distributionChannel?: string;
          };
        }
      ).newApplication;
      const draftMissing = getMissingRequiredAppFields(draft);
      if (draftMissing.length > 0 && draftMissing.length < totalRequired) {
        draftMissing.forEach((key) => {
          errors[`newApplication.${key}`] = {
            type: "required",
            message: requiredMessage,
          };
        });
      }
      return errors;
    };
    app.patchAppState({
      currentReport: currentProject,
      showEditFormConfirmationDialog: false,
      showChannelDeletionDialog: false,
      pendingChannelDeletionCount: undefined,
      showDeleteAppDialog: false,
      pendingDeleteAppId: undefined,
    });

    const channelCatalog = getDistributionChannels(
      configuration?.distributionChannelDefinition
    );
    const oeChannelIds = (
      currentProject?.project?.distributionChannels ?? []
    ).filter((id) => channelCatalog.some((c) => c.id === id));

    // pimo-form has no public tab-selection API, so the pinned button clicks the Add Application rail entry.
    const selectAddApplicationTab = async () => {
      const normalizeLabel = (text: string) =>
        text
          .replace(/\s+/g, " ")
          .replace(/^\+\s*/, "")
          .trim()
          .toLowerCase();

      const labelToChannelId = new Map(
        channelCatalog.map((channel) => [
          normalizeLabel(channel.label),
          channel.id,
        ])
      );

      const resolveActiveChannelId = (): string | undefined => {
        const rail = getAppTabRail();
        if (!rail) return undefined;

        const selectedCandidate =
          rail.querySelector<HTMLElement>(
            '[aria-selected="true"], .Mui-selected, .active'
          ) ?? undefined;
        const selectedLabel = normalizeLabel(
          selectedCandidate?.textContent ?? ""
        );
        const fromSelectedUi = labelToChannelId.get(selectedLabel);
        if (fromSelectedUi) return fromSelectedUi;

        const selectedChannels = normalizeDistributionChannelIds(
          editForm.getFormMethods()?.getValues?.("distributionChannels")
        );
        if (selectedChannels.length > 0) return selectedChannels[0];
        return undefined;
      };

      const activeChannelId = resolveActiveChannelId();

      if (activeChannelId) {
        const setValue = editForm.getFormMethods()?.setValue as
          ((name: string, value: unknown) => void) | undefined;
        setValue?.("newApplication.distributionChannel", activeChannelId);
      }
      const targetLabel = normalizeLabel(`Add ${itemName}`);

      const trySelect = (rail: Element | null | undefined): boolean => {
        const rows = Array.from(
          rail?.querySelectorAll<HTMLElement>("div") ?? []
        ).filter((row) => !row.closest(".vmd-add-application-slot"));

        for (const row of rows) {
          const rowLabel = normalizeLabel(row.textContent ?? "");
          if (rowLabel === targetLabel) {
            row.click();
            return true;
          }
        }
        return false;
      };

      // Always resolve from the real tab rail. The button lives in the navigation slot and has
      // its own "Add ..." text, which must never be used as click target.
      if (trySelect(getAppTabRail())) return;

      // Channel selection and tab rebuild can race the click. If the Add tab is not mounted yet,
      // force a rebuild and retry once from the current tab rail.
      await rebuildTabs?.();
      if (trySelect(getAppTabRail())) return;

      requestAnimationFrame(() => {
        trySelect(getAppTabRail());
      });
    };

    const selectNewlyCreatedItemTab = (
      customId: string | undefined,
      itemName: string
    ): boolean => {
      const addAppSlot = document.querySelector<HTMLElement>(
        ".vmd-add-application-slot"
      );
      if (!addAppSlot) return false;
      const rail = addAppSlot.parentElement?.parentElement?.firstElementChild;
      if (!rail) return false;
      const label = buildAppTabLabel(customId, itemName);
      for (const el of Array.from(rail.querySelectorAll<HTMLElement>("div"))) {
        if (el.textContent?.trim() === label) {
          el.click();
          return true;
        }
      }
      return false;
    };

    const trySelectNewlyCreatedItemTab = (
      customId: string | undefined,
      itemName: string,
      attemptsLeft = 10
    ) => {
      if (selectNewlyCreatedItemTab(customId, itemName)) return;
      if (attemptsLeft > 0) {
        setTimeout(
          () =>
            trySelectNewlyCreatedItemTab(customId, itemName, attemptsLeft - 1),
          150
        );
      }
    };

    // Creates the app immediately from the Add Application tab and appends it to the open form.
    const createNewApplication = async () => {
      // Skip if a create or a save is already running, so the app isn't created twice.
      if (isCreatingApp || isSaving) return;
      isCreatingApp = true;
      try {
        await runCreateNewApplication();
      } finally {
        isCreatingApp = false;
      }
    };
    const runCreateNewApplication = async () => {
      const formMethods = editForm.getFormMethods();
      const setValue = formMethods?.setValue as unknown as
        ((name: string, value: unknown) => void) | undefined;
      const newApplication = (
        formMethods?.getValues() as
          | (Project & {
              newApplication?: Partial<BasicItem> & {
                distributionChannel?: string[];
              };
            })
          | undefined
      )?.newApplication;
      const channel = newApplication?.distributionChannel;
      const name = newApplication?.name?.trim();

      const missingFields = getMissingRequiredAppFields(newApplication);
      setNewAppFieldErrors(
        formMethods,
        missingFields,
        getLocalizedMessage(
          app.getAppState().language || "en",
          "validationErrors.fieldRequired"
        ) || "This field is required"
      );
      if (missingFields.length > 0) {
        if (!formMethods) {
          showSnackbar(
            app as unknown as App<
              UniversalAppState & DefaultOverlayPartialAppState
            >,
            `Please select a distribution channel and enter a ${(configuration?.itemName ?? "Tracking Item").toLowerCase()} name.`,
            "error"
          );
        }
        return;
      }
      // Redundant with the check above, but narrows the types for the rest of the function.
      if (!channel?.length || !name) return;

      const basicItemValues = { ...newApplication };
      delete basicItemValues.distributionChannel;
      // The primary can never also be a secondary channel.
      if (Array.isArray(basicItemValues.otherDistributionChannels)) {
        basicItemValues.otherDistributionChannels =
          basicItemValues.otherDistributionChannels.filter(
            (c) => !channel.includes(c)
          );
      }

      const project = app.getAppState().currentReport?.project;
      if (!project) {
        return;
      }
      // Build the app with no id; it persists on the next Save (no eager POST).
      // Normalize to an array exactly like the Save-time staging path (runSubmit)
      // does for the same field — downstream tab-building (appsForChannel,
      // unassignedApps) calls array methods on distributionChannel and would
      // throw on the raw string a single-channel selection can produce.
      const newItem = {
        distributionChannel: Array.isArray(channel)
          ? channel
          : [channel].filter(Boolean),
        isChannelHeader: false,
        basicItem: { ...basicItemValues, name },
      } as unknown as ReportingItem;
      const reportingItems = [...(project.reportingItems ?? []), newItem];

      // Append only (never re-sort) so existing field indexes stay aligned with the mounted form values.
      setValue?.(`reportingItems.[${reportingItems.length - 1}]`, newItem);
      setValue?.("newApplication", {
        distributionChannel: "",
        name: "",
        customId: "",
        owner: "",
        recordType: "",
        description: "",
        criticality: "",
        lifeCycle: "",
        hasContracts: "",
        otherDistributionChannels: [],
        processSteps: [],
        applicationCapabilities: [],
        technicalExposure: [],
      });
      app.patchAppState({
        currentReport: { project: { ...project, reportingItems } },
      });
      await rebuildTabs?.();
      setTimeout(
        () => trySelectNewlyCreatedItemTab(basicItemValues.customId, name),
        150
      );
      showSnackbar(
        app as unknown as App<
          UniversalAppState & DefaultOverlayPartialAppState
        >,
        `${itemName} "${name}" added. Click Save to persist it.`,
        "success"
      );
    };

    const resolveInitialTabId = (rawTabId?: string): string => {
      if (!rawTabId) return "management-summary";
      if (configuration?.useCase !== "distribution") return rawTabId;
      const topLevelIds = [
        "management-summary",
        ...oeChannelIds.map((id) => `channel-${id}`),
        "channel-unassigned",
        "add-application",
      ];
      if (topLevelIds.includes(rawTabId)) return rawTabId;
      const item = currentProject?.project?.reportingItems?.find(
        (ri) => String(ri.basicItem?.id) === rawTabId
      );
      if (!item) return "management-summary";
      const channels = item.distributionChannel ?? [];
      const channel = channels.length > 0 ? channels[0] : "";
      return oeChannelIds.includes(channel)
        ? `channel-${channel}`
        : "channel-unassigned";
    };

    const normalizeDistributionChannelIds = (value: unknown): string[] => {
      if (typeof value === "string") {
        return Array.from(
          new Set(
            value
              .split(",")
              .map((id) => id.trim())
              .filter(Boolean)
          )
        );
      }

      if (!Array.isArray(value)) return [];

      const ids = value
        .map((entry) => {
          if (typeof entry === "string") return entry;
          if (typeof entry === "number") return String(entry);

          if (entry && typeof entry === "object") {
            const asObj = entry as {
              value?: unknown;
              id?: unknown;
              key?: unknown;
            };
            if (typeof asObj.value === "string") return asObj.value;
            if (typeof asObj.value === "number") return String(asObj.value);
            if (typeof asObj.id === "string") return asObj.id;
            if (typeof asObj.id === "number") return String(asObj.id);
            if (typeof asObj.key === "string") return asObj.key;
            if (typeof asObj.key === "number") return String(asObj.key);
          }

          return "";
        })
        .map((id) => id.trim())
        .filter(Boolean);

      return Array.from(new Set(ids));
    };

    form.mapState((state) => {
      // Extract the tab ID from URL query parameters
      const urlParams = new URLSearchParams(window.location.search);
      const initialSelectedTabId = resolveInitialTabId(
        urlParams.get("tabId") ?? undefined
      );

      const isDistribution = configuration?.useCase === "distribution";
      const liveChannelsRaw = (
        editForm.getFormMethods()?.getValues() as Project | undefined
      )?.distributionChannels;
      const selectedChannels = normalizeDistributionChannelIds(
        liveChannelsRaw ?? state.currentReport?.project?.distributionChannels
      );
      const hasChannels = channelCatalog.some((c) =>
        selectedChannels.includes(c.id)
      );

      return {
        id: state.currentReport?.project?.id ?? "",
        ...state.currentReport?.project,
        title:
          configuration?.useCase === "distribution"
            ? "Update Report"
            : `${
                getLocalizedMessage(state.language || "en", "report") ||
                "Report"
              }`,
        ...{
          validateFormData: (data: Omit<Project, "id">) =>
            computeFormErrors?.(data, state.language || "en") ?? {},
        },
        initialSelectedTabId,
        enableCloseButton: true,
        expandAllChildren: false,
        disableNavigationWithoutTarget: true,

        useWatchForEntireForm: false,
        actions: {
          "save-and-back": {
            label:
              getLocalizedMessage(state.language || "en", "back") || "Back",
          },
          "save-and-next": {
            label: "Next",
            variant: "outlined",
            sx: {
              color: theme.palette.primary.main,
              backgroundColor: "white",
              boxShadow: "none",
              border: "none",
            },

            position: 1,
          },
          "save-and-close": {
            label: "Save",
            sx: {
              color: "white",
              backgroundColor: theme.palette.primary.main,
              boxShadow: "none",
              "&.Mui-disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            },

            overrideValidationTarget: "all-tabs",
            position: 3,
          },

          cancel: {
            label:
              getLocalizedMessage(state.language || "en", "cancel") || "Cancel",
            position: 2,
          },
        },
        navigationSlot: (() => {
          if (isDistribution && hasChannels) {
            return (
              <Box className="vmd-add-application-slot" sx={{ p: 0 }}>
                <GlobalStyles
                  styles={{
                    "div:has(> div > .vmd-add-application-slot) > div:first-of-type > div:nth-last-of-type(-n+2)":
                      {
                        display: "none",
                      },

                    // Give the scrollable tab rail room so the last app rows aren't clipped

                    "div:has(> div > .vmd-add-application-slot) > div:first-of-type":
                      {
                        paddingBottom: "16px",
                        scrollPaddingBottom: "16px",
                      },

                    [`.MuiGrid2-root:not(.MuiGrid2-container):has(.${APP_STICKY_HEADER_CLASS})`]:
                      {
                        position: "sticky",
                        top: "-46px",
                        zIndex: 3,
                        backgroundColor: "#fff",
                        borderBottom: "1px solid #e0e0e0",
                        paddingTop: "46px",
                        marginTop: "-46px",
                      },
                  }}
                />
                <Box sx={{ px: 2, pb: 2, pt: 2 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Add />}
                    sx={{
                      textTransform: "none",
                      borderRadius: "4px",
                      padding: "7px 16px",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => void selectAddApplicationTab()}
                  >
                    Add {itemName}
                  </Button>
                </Box>
              </Box>
            );
          }

          return undefined;
        })(),
      };
    });

    let preloaded: typeof currentProject | undefined = currentProject;
    const buildTabs = async () => {
      const appState = app.getAppState();
      const language = appState.language;

      // Rebuilds reuse the in-memory project: a refetch could reorder reportingItems and break field-index alignment.
      const fetchedData =
        preloaded ??
        app.getAppState().currentReport ??
        (await fetchProject({ id }));
      preloaded = undefined;

      app.patchAppState({
        currentReport: fetchedData,
      });

      const isDistribution = configuration?.useCase === "distribution";
      const itemName = configuration?.itemName ?? "Tracking Item";
      const channelCatalog = getDistributionChannels(
        configuration?.distributionChannelDefinition
      );
      const validChannelIds = channelCatalog.map((channel) => channel.id);
      // Use the live (unsaved) channel selection on rebuilds; fall back to saved channels on the initial build.
      const liveSelectionRaw = (
        editForm.getFormMethods()?.getValues() as Project | undefined
      )?.distributionChannels;
      const oeChannels = orderDistributionChannels({
        selectedChannelIds: normalizeDistributionChannelIds(
          liveSelectionRaw ?? fetchedData?.project?.distributionChannels
        ),
        configuredChannelIds: validChannelIds,
      });

      const overallTab = editForm.addFormTab({
        label: isDistribution
          ? "General OE Data"
          : (appState.settings?.configuration?.projectName ??
            getLocalizedMessage(language || "en", "project") ??
            "Project"),
        id: "management-summary",
        validationIndicatorSlot: (
          <Error sx={{ color: theme.palette.error.main }} />
        ),
      });

      overallTab.addLayoutElement({
        type: "form-tab-title",
        label: `${
          fetchedData?.project?.customId
            ? fetchedData.project.customId + " - "
            : ""
        }${fetchedData?.project?.name ?? ""}`,
      });

      if (isDistribution) {
        overallTab.addFormField({
          type: "dropdown",
          label: "What distribution channels do you have in your OE?",
          name: "distributionChannels",
          multiSelect: true,
          sx: STYLE_TEXT,
          menuItemsSx: STYLE_TEXT,
          items: channelCatalog.map((channel) => ({
            value: channel.id,
            label: channel.label,
          })),
        });
      }

      overallTab.addFormField({
        type: MEMO_RICH_TEXT_FIELD_TYPE,
        label: `${
          getLocalizedMessage(language || "en", "managementSummary") ||
          "Management Summary"
        }${
          getFieldValidation(
            configuration,
            "managementSummary",
            "projectReporting"
          )
            ? "*"
            : ""
        }`,
        multiline: true,
        name: "managementSummary",
        maxLength: {
          value: CHARACTER_LIMITATIONS["managementSummary"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["managementSummary"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["managementSummary"]} characters or less`,
        },
        required: {
          value: true,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        sx: STYLE_TEXT,
        isVisible: () =>
          !isDistribution &&
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((f) => f.purpose === "projectReporting")
              ?.find((f) => f.fieldName === "managementSummary")?.enabled
          ),
      });

      overallTab.addFormField({
        type: MEMO_RICH_TEXT_FIELD_TYPE,
        label: `${
          getLocalizedMessage(language || "en", "challenges") || "Challenges"
        }${
          getFieldValidation(configuration, "challenges", "projectReporting")
            ? "*"
            : ""
        }`,
        multiline: true,
        name: "challenges",
        maxLength: {
          value: CHARACTER_LIMITATIONS["challenges"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["challenges"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["challenges"]} characters or less`,
        },
        required: {
          value: true,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        sx: STYLE_TEXT,
        isVisible: () =>
          !isDistribution &&
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((f) => f.purpose === "projectReporting")
              ?.find((f) => f.fieldName === "challenges")?.enabled
          ),
      });

      overallTab.addFormField({
        type: MEMO_RICH_TEXT_FIELD_TYPE,
        label: `${
          getLocalizedMessage(language || "en", "achievements") ||
          "Achievements"
        }${
          getFieldValidation(configuration, "achievements", "projectReporting")
            ? "*"
            : ""
        }`,
        multiline: true,
        name: "achievements",
        maxLength: {
          value: CHARACTER_LIMITATIONS["achievements"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["achievements"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["achievements"]} characters or less`,
        },
        required: {
          value: true,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        sx: STYLE_TEXT,
        isVisible: () =>
          !isDistribution &&
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((f) => f.purpose === "projectReporting")
              ?.find((f) => f.fieldName === "achievements")?.enabled
          ),
      });

      overallTab.addFormField({
        type: MEMO_RICH_TEXT_FIELD_TYPE,
        label: `${
          getLocalizedMessage(language || "en", "nextSteps") || "Next Steps"
        }${
          getFieldValidation(configuration, "nextSteps", "projectReporting")
            ? "*"
            : ""
        }`,
        multiline: true,
        name: "nextSteps",
        maxLength: {
          value: CHARACTER_LIMITATIONS["nextSteps"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["nextSteps"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["nextSteps"]} characters or less`,
        },
        required: {
          value: true,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        sx: STYLE_TEXT,
        isVisible: () =>
          !isDistribution &&
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((f) => f.purpose === "projectReporting")
              ?.find((f) => f.fieldName === "nextSteps")?.enabled
          ),
      });

      overallTab.addFormField({
        type: "number",
        label: `${
          getLocalizedMessage(language || "en", "spentBudget") || "Spent Budget"
        } (€)${
          getFieldValidation(configuration, "spentBudget", "projectReporting")
            ? "*"
            : ""
        }`,
        name: "spentBudget",
        maxLength: {
          value: CHARACTER_LIMITATIONS["spentBudget"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["spentBudget"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["spentBudget"]} characters or less`,
        },
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        sx: STYLE_TEXT,
        isVisible: () =>
          !isDistribution &&
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((f) => f.purpose === "projectReporting")
              ?.find((f) => f.fieldName === "spentBudget")?.enabled
          ),
      });

      for (const field of configuration?.customFields?.filter(
        (f) => !isDistribution && f.purpose === "projectReporting" && f.enabled
      ) ?? []) {
        // Find the matching custom field value for this project
        const customFieldValue = fetchedData?.project?.customFields?.find(
          (cfv) => cfv.customFieldDefinition?.id === field.id
        );
        const indexOfCustomFieldValue =
          fetchedData?.project?.customFields?.indexOf(customFieldValue!);
        overallTab.addFormField({
          type: field.type === "Select" ? "dropdown" : field.type.toLowerCase(),
          label: `${field.label}${field.mandatory ? "*" : ""}`,
          name: `customFields.[${indexOfCustomFieldValue}].value`,
          sx: STYLE_TEXT,
          uiOptions: {
            fieldSize: { xs: 6 },
          },
          isVisible: () => Boolean(field.enabled),
          ...(field.type === "Select"
            ? {
                items:
                  field.options?.map((option) => ({
                    value: option.name,
                    label: option.name,
                    color: option.color,
                  })) ?? [],
              }
            : {}),
        } as unknown as FormFieldDefinition);
      }

      // Distribution keeps reportingItems in append order — sorting in place would shift field indexes mid-session.
      const sortedItems =
        !isDistribution && fetchedData?.project?.reportingItems
          ? fetchedData?.project?.reportingItems.sort((a, b) =>
              sortByCustomIdAndName(
                { id: a.basicItem?.customId, name: a.basicItem?.name },
                { id: b.basicItem?.customId, name: b.basicItem?.name },
                "ASC"
              )
            )
          : [];

      type ReportTab = typeof overallTab;

      const buildAppReportingFields = (
        tab: FieldSink,
        reportingItem: ReportingItem
      ) => {
        tab.addFormField({
          type: "dropdown",
          label: `${
            getLocalizedMessage(language || "en", "status") || "Status"
          }${
            getFieldValidation(configuration, "status", "itemReporting")
              ? "*"
              : ""
          }`,
          name: `reportingItems.[${fetchedData?.project?.reportingItems?.indexOf(
            reportingItem
          )}].status`,
          sx: STYLE_TEXT,
          uiOptions: {
            fieldSize: { xs: 6 },
          },
          menuItemsSx: STYLE_TEXT,
          isVisible: () =>
            Boolean(
              configuration?.fieldsDefinition
                ?.filter((f) => f.purpose === "itemReporting")
                ?.find(
                  (f) =>
                    (f.fieldName === "status" &&
                      f.enabled &&
                      configuration.useCase !== "pmo") ||
                    (configuration.useCase === "pmo" &&
                      configuration.statusReportingLevel === "overall" &&
                      f.fieldName === "status" &&
                      f.enabled)
                )
            ),
          items:
            configuration?.statusDefinition?.map((status) => ({
              value: status.name,
              label: status.name,
              color: status.color,
            })) ?? [],
        });

        tab.addFormField({
          type: "dropdown",
          label: `Answer ${
            getFieldValidation(configuration, "answer", "itemReporting")
              ? "*"
              : ""
          }`,
          name: `reportingItems.[${fetchedData?.project?.reportingItems?.indexOf(
            reportingItem
          )}].answer`,
          sx: STYLE_TEXT,
          uiOptions: {
            fieldSize: { xs: 6 },
          },
          menuItemsSx: STYLE_TEXT,
          isVisible: () =>
            Boolean(
              configuration?.fieldsDefinition
                ?.filter((f) => f.purpose === "itemReporting")
                ?.find((f) => f.fieldName === "answer" && f.enabled)
            ),
          items:
            configuration?.answerDefinition?.map((answer) => ({
              value: answer.name,
              label: answer.name,
            })) ?? [],
        });

        configuration?.fieldsDefinition
          ?.filter((field) => field.purpose === "itemReporting")
          ?.find(
            (f) =>
              f.fieldName === "status" &&
              f.enabled &&
              configuration.useCase === "pmo" &&
              configuration.statusReportingLevel === "category"
          ) &&
          configuration?.semaphoreStatusDefinition?.forEach((status, index) =>
            tab.addFormField({
              type: "dropdown",
              uiOptions: {
                fieldSize: { xs: 6 },
              },
              label: `Status ${status.name}${
                getFieldValidation(configuration, "status", "itemReporting")
                  ? "*"
                  : ""
              }`,
              name: `reportingItems.[${fetchedData?.project?.reportingItems?.indexOf(
                reportingItem
              )}].semaphoreStatus${index + 1}`,

              items:
                configuration?.statusDefinition?.map((status) => ({
                  value: status.name,
                  label: `${status.name}`,
                  color: status.color,
                })) ?? [],
            })
          );

        tab.addFormField({
          type: "dropdown",
          uiOptions: {
            fieldSize: { xs: 6 },
          },
          label: `${
            getLocalizedMessage(language || "en", "degreeOfCompletion") ||
            "Degree of completion"
          }${
            getFieldValidation(configuration, "completion", "itemReporting")
              ? "*"
              : ""
          }`,
          name: `reportingItems.[${fetchedData?.project?.reportingItems?.indexOf(
            reportingItem
          )}].degreeOfCompletion`,
          sx: STYLE_TEXT,
          menuItemsSx: STYLE_TEXT,
          isVisible: () =>
            Boolean(
              configuration?.fieldsDefinition
                ?.filter((f) => f.purpose === "itemReporting")
                ?.find((f) => f.fieldName === "completion")?.enabled
            ),
          items:
            configuration?.degreeOfCompletionDefinition?.map((completion) => ({
              value: completion.name,
              label: completion.name,
              color: completion.color,
            })) ?? [],
        });

        tab.addFormField({
          type: "dropdown",
          uiOptions: {
            fieldSize: { xs: 6 },
          },
          label: `${
            getLocalizedMessage(language || "en", "progress") || "Progress"
          }${
            getFieldValidation(configuration, "progress", "itemReporting")
              ? "*"
              : ""
          }`,
          sx: STYLE_TEXT,
          menuItemsSx: STYLE_TEXT,
          isVisible: () =>
            Boolean(
              configuration?.fieldsDefinition
                ?.filter((f) => f.purpose === "itemReporting")
                ?.find((f) => f.fieldName === "progress")?.enabled
            ),
          name: `reportingItems.[${fetchedData?.project?.reportingItems?.indexOf(
            reportingItem
          )}].progress`,
          items: Array.from({ length: 21 }, (_, i) => i * 5).map((value) => ({
            value: String(value),
            label: `${value}%`,
          })),
        });
        tab.addFormField({
          type: "number",
          uiOptions: {
            fieldSize: { xs: 6 },
          },
          label: `${
            getLocalizedMessage(language || "en", "spentBudget") ||
            "Spent Budget"
          } (€) ${
            getFieldValidation(configuration, "spentBudget", "itemReporting")
              ? "*"
              : ""
          }`,
          sx: STYLE_TEXT,
          isVisible: () =>
            Boolean(
              configuration?.fieldsDefinition
                ?.filter((f) => f.purpose === "itemReporting")
                ?.find((f) => f.fieldName === "spentBudget")?.enabled
            ),
          name: `reportingItems.[${fetchedData?.project?.reportingItems?.indexOf(
            reportingItem
          )}].spentBudget`,
        });

        tab.addFormField({
          type: MEMO_RICH_TEXT_FIELD_TYPE,

          label: `Comment ${
            getFieldValidation(configuration, "comment", "itemReporting")
              ? "*"
              : ""
          }`,
          sx: STYLE_TEXT,
          isVisible: () =>
            Boolean(
              configuration?.fieldsDefinition
                ?.filter((field) => field.purpose === "itemReporting")
                ?.find((f) => f.fieldName === "comment")?.enabled
            ),
          multiline: true,
          name: `reportingItems.[${fetchedData?.project?.reportingItems?.indexOf(
            reportingItem
          )}].comment`,
          maxLength: {
            value: CHARACTER_LIMITATIONS["basicItems.{idx}.comment"],
            message:
              getLocalizedMessage(
                language || "en",
                "validationErrors.maxLength",
                { maxLength: CHARACTER_LIMITATIONS["basicItems.{idx}.comment"] }
              ) ||
              `Must be ${CHARACTER_LIMITATIONS["basicItems.{idx}.comment"]} characters or less`,
          },
        });
        for (const field of configuration?.customFields?.filter(
          (f) => f.purpose === "itemReporting" && f.enabled
        ) ?? []) {
          // Find the matching custom field value for this specific reporting item
          const customFieldValue = reportingItem.customFields?.find(
            (cfv) => cfv.customFieldDefinition?.id === field.id
          );
          const indexOfReportingItem =
            fetchedData?.project?.reportingItems?.indexOf(reportingItem);
          const indexOfCustomFieldValue = reportingItem.customFields?.indexOf(
            customFieldValue!
          );

          tab.addFormField({
            type:
              field.type === "Select" ? "dropdown" : field.type.toLowerCase(),
            uiOptions: {
              fieldSize: { xs: 6 },
            },
            label: `${field.label}${field.mandatory ? "*" : ""}`,
            name: `reportingItems.[${indexOfReportingItem}].customFields.[${indexOfCustomFieldValue}].value`,
            sx: STYLE_TEXT,
            isVisible: () => Boolean(field.enabled),
            ...(field.type === "Select"
              ? {
                  items:
                    field.options?.map((option) => ({
                      value: option.name,
                      label: option.name,
                      color: option.color,
                    })) ?? [],
                }
              : {}),
          } as unknown as FormFieldDefinition);
        }
        const attachmentField = configuration?.fieldsDefinition?.find(
          (field) =>
            field.fieldName === "attachments" &&
            field.purpose === "itemReporting"
        );

        if (attachmentField?.enabled) {
          tab.addFormField({
            type: "attachment",
            label: attachmentField.label || "Supporting Documents",
            name: `reportingItems.[${fetchedData?.project?.reportingItems?.indexOf(
              reportingItem
            )}].attachments`,
            entityId: reportingItem.id!,
            onFetchAttachments: fetchAttachmentsByReportingItem,
            onUploadAttachment: uploadAttachmentTracked,
            onDeleteAttachment: deleteAttachment,
            onDownloadAttachment: downloadAttachment,
            required:
              attachmentField.isMandatory === true
                ? {
                    value: true,
                    message: "At least one attachment is required",
                  }
                : undefined,
          });
        }
      };

      // Sticky header row: app title on the left, the create/delete action on the right.
      const addAppHeader = (
        tab: FieldSink,
        title: string,
        action: {
          name: string;
          label: string;
          onClick: () => void;
          icon: "add" | "delete";
          color: "primary" | "error";
        }
      ) => {
        tab.addFormField({
          type: ACTION_BUTTON_FIELD_TYPE,
          name: action.name,
          headerTitle: title,
          buttonLabel: action.label,
          buttonIcon: action.icon,
          buttonColor: action.color,
          onButtonClick: action.onClick,
        });
      };

      // Shared "this field is required" rule for the mandatory app fields.
      const requiredFieldRule = {
        value: true,
        message:
          getLocalizedMessage(
            language || "en",
            "validationErrors.fieldRequired"
          ) || "This field is required",
      };

      // Whether a Master Data DC System itemBasic field is enabled in Settings.
      const isItemBasicEnabled = (fieldName: string) =>
        Boolean(
          configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "itemBasic")
            ?.find((f) => f.fieldName === fieldName)?.enabled
        );

      // Primary + secondary channels rendered as one pair. Secondary excludes the primary.
      const buildChannelFields = (
        tab: FieldSink,
        primaryFieldName: string,
        otherFieldName: string,
        primaryChannel?: string | string[],
        required?: { value: boolean; message: string }
      ) => {
        tab.addFormField({
          type: "dropdown",
          label: "Primary Distribution Channel*",
          name: primaryFieldName,
          sx: STYLE_TEXT,
          menuItemsSx: STYLE_TEXT,
          uiOptions: { fieldSize: { xs: 6 } },
          required,
          multiSelect: true,
          items: oeChannels.map((channel) => ({
            value: channel,
            label: getChannelLabel(channel, channelCatalog),
          })),
        });
        tab.addFormField({
          type: "dropdown",
          label: `Other Distribution Channels${
            getFieldValidation(
              configuration,
              "otherDistributionChannels",
              "itemBasic"
            )
              ? "*"
              : ""
          }`,
          name: otherFieldName,
          multiSelect: true,
          // Disabled until a primary is chosen; the primary is then excluded from the options.
          disabled:
            !primaryChannel ||
            (Array.isArray(primaryChannel) && primaryChannel.length === 0),
          sx: STYLE_TEXT,
          menuItemsSx: STYLE_TEXT,
          uiOptions: { fieldSize: { xs: 6 } },
          isVisible: () => isItemBasicEnabled("otherDistributionChannels"),
          items: oeChannels
            .filter(
              (channel) =>
                !primaryChannel ||
                !(Array.isArray(primaryChannel)
                  ? primaryChannel.includes(channel)
                  : primaryChannel === channel)
            )
            .map((channel) => ({
              value: channel,
              label: getChannelLabel(channel, channelCatalog),
            })),
        });
      };

      // Shared standard app field set for saved apps (reportingItems[idx].basicItem) and the Add Application tab.
      const buildAppFormFields = (
        tab: FieldSink,
        namePrefix: string,
        contractsLabel: string,
        opts?: { afterFields?: () => void; skipNameValidation?: boolean }
      ) => {
        const requiredRule = opts?.skipNameValidation
          ? undefined
          : requiredFieldRule;
        // Master Data DC System fields (recordType, criticality, ...) carry their own
        // enabled/mandatory settings instead of the static requiredRule above.
        const itemBasicRequiredRule = (fieldName: string) =>
          !opts?.skipNameValidation &&
          getFieldValidation(configuration, fieldName, "itemBasic")
            ? {
                value: true,
                message:
                  getLocalizedMessage(
                    language || "en",
                    "validationErrors.fieldRequired"
                  ) || "This field is required",
              }
            : undefined;
        tab.addFormField({
          type: "text",
          label: `${itemName} Name*`,
          name: `${namePrefix}.name`,
          multiline: false,
          sx: STYLE_TEXT,
          uiOptions: { fieldSize: { xs: 6 } },
          required: requiredRule,
        });
        tab.addFormField({
          type: "text",
          label: "Archer ID*",
          name: `${namePrefix}.customId`,
          multiline: false,
          sx: STYLE_TEXT,
          uiOptions: { fieldSize: { xs: 6 } },
          required: requiredRule,
        });
        tab.addFormField({
          type: "text",
          label: `${itemName} Owner`,
          name: `${namePrefix}.owner`,
          multiline: false,
          sx: STYLE_TEXT,
          uiOptions: { fieldSize: { xs: 6 } },
        });
        tab.addFormField({
          type: "dropdown",
          label: `Record Type${
            getFieldValidation(configuration, "recordType", "itemBasic")
              ? "*"
              : ""
          }`,
          name: `${namePrefix}.recordType`,
          required: itemBasicRequiredRule("recordType"),
          sx: STYLE_TEXT,
          menuItemsSx: STYLE_TEXT,
          uiOptions: { fieldSize: { xs: 6 } },
          isVisible: () => isItemBasicEnabled("recordType"),
          items: VISIBLE_APP_RECORD_TYPES.map((value) => ({
            value,
            label: value,
          })),
        });
        tab.addFormField({
          type: MEMO_RICH_TEXT_FIELD_TYPE,
          label: `${itemName} Description*`,
          name: `${namePrefix}.description`,
          multiline: true,
          sx: STYLE_TEXT,
          required: requiredRule,
          maxLength: {
            value: 5000,
            message:
              getLocalizedMessage(
                language || "en",
                "validationErrors.maxLength",
                {
                  maxLength: 5000,
                }
              ) || "Must be 5000 characters or less",
          },
        });
        tab.addFormField({
          type: "dropdown",
          label: `Process Steps${
            getFieldValidation(configuration, "processSteps", "itemBasic")
              ? "*"
              : ""
          }`,
          name: `${namePrefix}.processSteps`,
          multiSelect: true,
          sx: STYLE_TEXT,
          menuItemsSx: STYLE_TEXT,
          required: itemBasicRequiredRule("processSteps"),
          isVisible: () => isItemBasicEnabled("processSteps"),
          items: Object.values(PROCESS_STEPS).map((value) => ({
            value,
            label: value,
          })),
        });
        tab.addFormField({
          type: "dropdown",
          label: `Application Capabilities${
            getFieldValidation(
              configuration,
              "applicationCapabilities",
              "itemBasic"
            )
              ? "*"
              : ""
          }`,
          name: `${namePrefix}.applicationCapabilities`,
          multiSelect: true,
          sx: STYLE_TEXT,
          menuItemsSx: STYLE_TEXT,
          required: itemBasicRequiredRule("applicationCapabilities"),
          isVisible: () => isItemBasicEnabled("applicationCapabilities"),
          items: Object.values(APPLICATION_CAPABILITIES).map((value) => ({
            value,
            label: value,
          })),
        });
        tab.addFormField({
          type: "dropdown",
          label: `Technical Exposure${
            getFieldValidation(configuration, "technicalExposure", "itemBasic")
              ? "*"
              : ""
          }`,
          name: `${namePrefix}.technicalExposure`,
          multiSelect: true,
          sx: STYLE_TEXT,
          menuItemsSx: STYLE_TEXT,
          required: itemBasicRequiredRule("technicalExposure"),
          isVisible: () => isItemBasicEnabled("technicalExposure"),
          items: Object.values(TECHNICAL_EXPOSURE).map((value) => ({
            value,
            label: value,
          })),
        });
        tab.addFormField({
          type: "dropdown",
          label: `Criticality of the ${itemName}${
            getFieldValidation(configuration, "criticality", "itemBasic")
              ? "*"
              : ""
          }`,
          name: `${namePrefix}.criticality`,
          sx: STYLE_TEXT,
          menuItemsSx: STYLE_TEXT,
          uiOptions: { fieldSize: { xs: 6 } },
          required: itemBasicRequiredRule("criticality"),
          isVisible: () => isItemBasicEnabled("criticality"),
          items: Object.values(APP_CRITICALITIES).map((value) => ({
            value,
            label: value,
          })),
        });
        tab.addFormField({
          type: "dropdown",
          label: `Life Cycle${
            getFieldValidation(configuration, "lifeCycle", "itemBasic")
              ? "*"
              : ""
          }`,
          name: `${namePrefix}.lifeCycle`,
          sx: STYLE_TEXT,
          menuItemsSx: STYLE_TEXT,
          uiOptions: { fieldSize: { xs: 6 } },
          required: itemBasicRequiredRule("lifeCycle"),
          isVisible: () => isItemBasicEnabled("lifeCycle"),
          items: Object.values(APP_LIFE_CYCLES).map((value) => ({
            value,
            label: value,
          })),
        });
        tab.addFormField({
          type: "radio",
          label: `${contractsLabel}${
            getFieldValidation(configuration, "hasContracts", "itemBasic")
              ? "*"
              : ""
          }`,
          name: `${namePrefix}.hasContracts`,
          sx: STYLE_TEXT,
          required: itemBasicRequiredRule("hasContracts"),
          isVisible: () => isItemBasicEnabled("hasContracts"),
          items: [
            { value: "yes", label: "yes" },
            { value: "no", label: "no" },
          ],
        });
        opts?.afterFields?.();
      };

      const buildAppDetailFields = (
        tab: FieldSink,
        reportingItem: ReportingItem
      ) => {
        const idx =
          fetchedData?.project?.reportingItems?.indexOf(reportingItem);
        const basicItemId = reportingItem.basicItem?.id;
        const channelLabel = (reportingItem.distributionChannel ?? [])
          .map((id) => getChannelLabel(id, channelCatalog))
          .join(", ");
        addAppHeader(
          tab,
          reportingItem.basicItem?.name ??
            reportingItem.basicItem?.id?.toString() ??
            itemName,
          {
            name: `delete-app-${basicItemId}`,
            label: `Delete ${itemName}`,
            icon: "delete",
            color: "error",
            onClick: () => {
              if (!basicItemId) return;
              app.patchAppState({
                showDeleteAppDialog: true,
                pendingDeleteAppId: basicItemId,
              });
            },
          }
        );
        // Editable primary channel — moving it re-buckets the app into another channel tab on save.
        // Use the live form value (not the fetched one) so the secondary's options/disabled state
        // track the current primary after an unsaved change.
        const detailGetValues = editForm.getFormMethods()?.getValues as
          ((name?: string) => unknown) | undefined;
        const livePrimaryChannel =
          (detailGetValues?.(`reportingItems.[${idx}].distributionChannel`) as
            string[] | undefined) ?? reportingItem.distributionChannel;
        buildChannelFields(
          tab,
          `reportingItems.[${idx}].distributionChannel`,
          `reportingItems.[${idx}].basicItem.otherDistributionChannels`,
          livePrimaryChannel,
          requiredFieldRule
        );
        buildAppFormFields(
          tab,
          `reportingItems.[${idx}].basicItem`,
          `Do you have contracts with Distribution Channel ${channelLabel}?`,
          {
            afterFields: () => {
              const attachmentField = configuration?.fieldsDefinition?.find(
                (field) =>
                  field.fieldName === "attachments" &&
                  field.purpose === "itemReporting"
              );
              if (!attachmentField?.enabled) {
                return;
              }
              const hasContracts = (formData: Record<string, unknown>) =>
                (
                  formData?.reportingItems as
                    { basicItem?: { hasContracts?: string } }[] | undefined
                )?.[idx ?? -1]?.basicItem?.hasContracts === "yes";

              // For a saved app (has a real RI id) use the real upload endpoint.
              // For a staged-but-not-yet-saved app (no id), buffer in pendingNewAppFiles
              // exactly like the "New Application" tab does — keyed by channel|name.
              if (reportingItem.id) {
                tab.addFormField({
                  type: "attachment",
                  label: "Please Upload your contracts here:",
                  name: `reportingItems.[${idx}].attachments`,
                  entityId: reportingItem.id,
                  isVisible: hasContracts,
                  onFetchAttachments: fetchAttachmentsByReportingItem,
                  onUploadAttachment: uploadAttachmentTracked,
                  onDeleteAttachment: deleteAttachment,
                  onDownloadAttachment: downloadAttachment,
                  required:
                    attachmentField.isMandatory === true
                      ? {
                          value: true,
                          message: "At least one attachment is required",
                        }
                      : undefined,
                });
              } else {
                // Unsaved app: resolve the pending key from the item itself (not form state).
                const ch = reportingItem.distributionChannel ?? "";
                const nm = reportingItem.basicItem?.name?.trim() ?? "";
                const pendingKey = ch && nm ? `${ch}|${nm}` : undefined;
                tab.addFormField({
                  type: "attachment",
                  label: "Please Upload your contracts here:",
                  name: `reportingItems.[${idx}].attachments`,
                  entityId: `pending-${ch}-${nm}`,
                  isVisible: hasContracts,
                  onFetchAttachments: async () => {
                    if (!pendingKey) return [];
                    const files = pendingNewAppFiles.get(pendingKey) ?? [];
                    return files.map(
                      (f) =>
                        ({
                          id: `pending:${pendingKey}:${f.name}`,
                          originalName: f.name,
                          fileName: f.name,
                          temporary: true,
                        }) as unknown as Attachment
                    );
                  },
                  onUploadAttachment: async (_entityId: string, file: File) => {
                    if (!pendingKey) return null;
                    pendingNewAppFiles.set(pendingKey, [
                      ...(pendingNewAppFiles.get(pendingKey) ?? []),
                      file,
                    ]);
                    return {
                      id: `pending:${pendingKey}:${file.name}`,
                      originalName: file.name,
                      fileName: file.name,
                      temporary: true,
                    } as unknown as Attachment;
                  },
                  onDeleteAttachment: async (attachmentId: string) => {
                    if (attachmentId.startsWith("pending:") && pendingKey) {
                      const fileName = attachmentId
                        .split(":")
                        .slice(2)
                        .join(":");
                      const files = pendingNewAppFiles.get(pendingKey);
                      if (files) {
                        const updated = files.filter(
                          (f) => f.name !== fileName
                        );
                        if (updated.length > 0) {
                          pendingNewAppFiles.set(pendingKey, updated);
                        } else {
                          pendingNewAppFiles.delete(pendingKey);
                        }
                      }
                      return true;
                    }
                    return deleteAttachment(attachmentId);
                  },
                  onDownloadAttachment: downloadAttachment,
                  required:
                    attachmentField.isMandatory === true
                      ? {
                          value: true,
                          message: "At least one attachment is required",
                        }
                      : undefined,
                });
              }
            },
          }
        );
      };

      const makeAppTab = (
        parentTab: ReportTab,
        reportingItem: ReportingItem
      ) => {
        const label = buildAppTabLabel(
          reportingItem.basicItem?.customId,
          reportingItem.basicItem?.name
        );
        const fullLabel = `${reportingItem.basicItem?.customId ?? ""} - ${
          reportingItem.basicItem?.name ?? ""
        }`.trim();
        const appTabId = `${reportingItem.basicItem?.id?.toString()}`;
        appLabelByTabId.set(appTabId, label);
        const tab = parentTab.addChildTab({
          label,
          fullLabel,
          id: appTabId,
          validationIndicatorSlot: (
            <Error sx={{ color: theme.palette.error.main }} />
          ),
        });

        tab.addFormField({
          type: REPORT_TAB_VISIBILITY_FIELD_TYPE,
          name: `${ACTIVE_APP_TAB_FIELD}__tracker`,
          appTabId,
          activeFieldName: ACTIVE_APP_TAB_FIELD,
          onActivate: () => onAppTabActivated(appTabId),
        });
        // Fields are built lazily: skip them until the tab has been opened once.
        if (!builtAppTabIds.has(appTabId)) return;
        const gatedTab = withActiveAppTabGate(tab, appTabId);
        if (isDistribution) {
          // Distribution renders its title inside the sticky header (with the Delete action).
          buildAppDetailFields(gatedTab, reportingItem);
        } else {
          gatedTab.addLayoutElement({
            type: "form-tab-title",
            label: `${
              reportingItem.basicItem?.name ??
              reportingItem.basicItem?.id?.toString()
            }`,
          });
          buildAppReportingFields(gatedTab, reportingItem);
        }
      };

      // A parent tab plus the apps that belong under it. Its tab is only materialised when it has
      // at least one app (see buildAppTabs).
      type AppContainer = {
        id: string;
        makeTab: () => ReportTab;
        apps: ReportingItem[];
        allowEmpty?: boolean;
      };

      // Builds every app tab up front. Each app's fields are gated by withActiveAppTabGate
      // (isVisible: only the active tab) and assembled lazily on first open (see onAppTabActivated),
      // so pimo mounts just the selected tab's fields — the rest stay empty shells. That keeps the
      // form responsive without paging.
      const buildAppTabs = (containers: AppContainer[]) => {
        for (const container of containers) {
          const apps = container.apps.filter(Boolean);
          if (apps.length === 0 && !container.allowEmpty) continue;
          const tab = container.makeTab();
          for (const app of apps) makeAppTab(tab, app);
        }
      };

      if (!isDistribution) {
        buildAppTabs([
          {
            id: "reporting-items",
            makeTab: () =>
              editForm.addFormTab({
                label:
                  ITEM_NAMES.find(
                    (name) =>
                      name.single === appState.settings?.configuration?.itemName
                  )?.plural ?? "Reporting Items",
                id: "reporting-items",
                validationIndicatorSlot: (
                  <Error sx={{ color: theme.palette.error.main }} />
                ),
              }),
            apps: sortedItems.filter((ri): ri is ReportingItem => Boolean(ri)),
          },
        ]);
      } else {
        const allItems = fetchedData?.project?.reportingItems ?? [];
        const appsForChannel = (channelId: string) =>
          allItems
            .filter(
              (ri) =>
                !ri.isChannelHeader &&
                (ri.distributionChannel ?? []).includes(channelId)
            )
            .sort((a, b) =>
              sortByCustomIdAndName(
                { id: a.basicItem?.customId, name: a.basicItem?.name },
                { id: b.basicItem?.customId, name: b.basicItem?.name },
                "ASC"
              )
            );

        const containers: AppContainer[] = oeChannels.map((channel) => ({
          id: `channel-${channel}`,
          allowEmpty: true,
          makeTab: () =>
            editForm.addFormTab({
              label: getChannelLabel(channel, channelCatalog),
              id: `channel-${channel}`,
              validationIndicatorSlot: (
                <Error sx={{ color: theme.palette.error.main }} />
              ),
            }),
          apps: appsForChannel(channel),
        }));

        const channelIds: string[] = oeChannels;
        const unassignedApps = allItems
          .filter(
            (ri) =>
              !ri.isChannelHeader &&
              !(ri.distributionChannel ?? []).some((c) =>
                channelIds.includes(c)
              )
          )
          .sort((a, b) =>
            sortByCustomIdAndName(
              { id: a.basicItem?.customId, name: a.basicItem?.name },
              { id: b.basicItem?.customId, name: b.basicItem?.name },
              "ASC"
            )
          );
        if (unassignedApps.length > 0) {
          containers.push({
            id: "channel-unassigned",
            makeTab: () =>
              editForm.addFormTab({
                label: "Unassigned",
                id: "channel-unassigned",
                validationIndicatorSlot: (
                  <Error sx={{ color: theme.palette.error.main }} />
                ),
              }),
            apps: unassignedApps,
          });
        }

        buildAppTabs(containers);

        // Generic create form at the bottom of the tab rail: pick a channel, fill the standard fields, create.
        if (oeChannels.length > 0) {
          const addAppTab = editForm.addFormTab({
            label: addApplicationTabLabel,
            id: "add-application",
          });
          addAppHeader(addAppTab, `New ${itemName}`, {
            name: "create-application",
            label: `Save ${itemName}`,
            icon: "add",
            color: "primary",
            onClick: () => void createNewApplication(),
          });
          const newAppPrimaryChannel = (
            editForm.getFormMethods()?.getValues() as
              | (Project & {
                  newApplication?: { distributionChannel?: string };
                })
              | undefined
          )?.newApplication?.distributionChannel;
          buildChannelFields(
            addAppTab,
            "newApplication.distributionChannel",
            "newApplication.otherDistributionChannels",
            newAppPrimaryChannel
          );
          const attachmentFieldForNewApp =
            configuration?.fieldsDefinition?.find(
              (field) =>
                field.fieldName === "attachments" &&
                field.purpose === "itemReporting"
            );
          buildAppFormFields(
            addAppTab,
            "newApplication",
            "Do you have contracts with the selected Distribution Channel?",
            {
              skipNameValidation: true,
              afterFields: () => {
                if (!attachmentFieldForNewApp?.enabled) return;
                const hasContractsForNew = (
                  formData: Record<string, unknown>
                ) =>
                  (
                    formData?.newApplication as
                      { hasContracts?: string } | undefined
                  )?.hasContracts === "yes";
                const getPendingKey = () => {
                  const na = (
                    editForm.getFormMethods()?.getValues() as
                      | (Project & {
                          newApplication?: { distributionChannel?: string };
                        })
                      | undefined
                  )?.newApplication as
                    | (Partial<BasicItem> & { distributionChannel?: string })
                    | undefined;
                  const ch = na?.distributionChannel;
                  const nm = na?.name?.trim();
                  if (!ch || !nm) return undefined;
                  return `${ch}|${nm}`;
                };
                addAppTab.addFormField({
                  type: "attachment",
                  label: "Please Upload your contracts here:",
                  name: "newApplication.pendingAttachments",
                  entityId: "pending-new-app",
                  isVisible: hasContractsForNew,
                  onFetchAttachments: async () => {
                    const key = getPendingKey();
                    if (!key) return [];
                    const files = pendingNewAppFiles.get(key) ?? [];
                    return files.map(
                      (f) =>
                        ({
                          id: `pending:${key}:${f.name}`,
                          originalName: f.name,
                          fileName: f.name,
                          temporary: true,
                        }) as unknown as Attachment
                    );
                  },
                  onUploadAttachment: async (_entityId: string, file: File) => {
                    const na = (
                      editForm.getFormMethods()?.getValues() as
                        | (Project & {
                            newApplication?: { distributionChannel?: string };
                          })
                        | undefined
                    )?.newApplication as
                      | (Partial<BasicItem> & { distributionChannel?: string })
                      | undefined;
                    const ch = na?.distributionChannel;
                    const nm = na?.name?.trim();
                    if (!ch || !nm) {
                      showSnackbar(
                        app as unknown as App<
                          UniversalAppState & DefaultOverlayPartialAppState
                        >,
                        `Please select a distribution channel and enter a ${itemName.toLowerCase()} name before uploading.`,
                        "error"
                      );
                      return null;
                    }
                    // Hold the file in memory; it is uploaded to the app once Save creates it.
                    const key = `${ch}|${nm}`;
                    pendingNewAppFiles.set(key, [
                      ...(pendingNewAppFiles.get(key) ?? []),
                      file,
                    ]);
                    return {
                      id: `pending:${key}:${file.name}`,
                      originalName: file.name,
                      fileName: file.name,
                      temporary: true,
                    } as unknown as Attachment;
                  },
                  onDeleteAttachment: async (attachmentId: string) => {
                    // Pending attachments (not yet saved) are keyed by "pending:${key}:${name}".
                    // Remove the file from the in-memory map instead of calling the real delete endpoint.
                    if (attachmentId.startsWith("pending:")) {
                      const parts = attachmentId.split(":");
                      // Format: "pending:<ch>|<nm>:<filename>" — key is parts[1], name is parts[2]
                      const key = parts[1];
                      const fileName = parts.slice(2).join(":");
                      const files = pendingNewAppFiles.get(key);
                      if (files) {
                        const updated = files.filter(
                          (f) => f.name !== fileName
                        );
                        if (updated.length > 0) {
                          pendingNewAppFiles.set(key, updated);
                        } else {
                          pendingNewAppFiles.delete(key);
                        }
                      }
                      return true;
                    }
                    return deleteAttachment(attachmentId);
                  },
                  onDownloadAttachment: downloadAttachment,
                });
              },
            }
          );
        }
      }
    };

    // Serialize rebuilds: concurrent builds interleave on the shared tab array and produce duplicate tabs.
    let rebuildChain: Promise<void> = Promise.resolve();
    rebuildTabs = () => {
      rebuildChain = rebuildChain.then(async () => {
        await editForm.buildDynamicTabsAsynchronously(buildTabs);
        // A child tab added after the last top-level tab needs one more render to appear (only addFormTab updates tab state).
        app.patchAppState({
          currentReport: { ...app.getAppState().currentReport },
        });
      });
      return rebuildChain;
    };

    const setupChannelWatchers = () => {
      let rebuildQueued = false;

      const queueRebuild = () => {
        if (rebuildQueued) return;
        rebuildQueued = true;
        Promise.resolve().then(() => {
          rebuildQueued = false;
          void rebuildTabs?.();
        });
      };

      const stopFormWatcher = setupFormFieldWatcher({
        getFormMethods: () => editForm.getFormMethods() as FormMethodsLike,
        rules: [
          {
            match: /^distributionChannels(?:$|\.|\[)/,
            onChange: () => {
              queueRebuild();
            },
          },
          {
            match: "newApplication.distributionChannel",
            onChange: ({ formMethods }) => {
              ensurePrimaryNotInSecondary(
                formMethods,
                "newApplication.distributionChannel",
                "newApplication.otherDistributionChannels"
              );
              queueRebuild();
            },
          },
          {
            match: /^reportingItems\.\[(\d+)\]\.distributionChannel$/,
            onChange: ({ formMethods, match }) => {
              const idx = match?.[1];
              if (!idx) return;
              ensurePrimaryNotInSecondary(
                formMethods,
                `reportingItems.[${idx}].distributionChannel`,
                `reportingItems.[${idx}].basicItem.otherDistributionChannels`
              );
              queueRebuild();
            },
          },
          {
            match: "newApplication.hasContracts",
            onChange: () => forceFormRerender(),
          },
          {
            match: /^reportingItems\.\[(\d+)\]\.basicItem\.hasContracts$/,
            onChange: () => forceFormRerender(),
          },
        ],
      });

      stopChannelWatchers = () => {
        stopFormWatcher();
      };
    };

    // Initial build goes through the same serialized queue so it can't race an early channel-change rebuild.
    await rebuildTabs?.();
    setupChannelWatchers();
  });
}
